import { useEffect, useMemo, useState } from 'react'
import moment from 'moment'

// BL
import { DATE_FORMATS } from '@/utils/dateHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { faPencil, faCheck } from '@fortawesome/pro-regular-svg-icons'
import { ManualTransactionModel } from '@/reducers/admin/allTransactions.slice'
import { userUploadDocument } from '@/actions/awsActions'
import { parseErrorFromCatch } from '@/utils/errorHelpers'
import { getCurrentUser } from '@/selectors/user.selectors'
import {
  validateMultipleManualTransactions,
  createMultipleManualTransactions,
} from '@/features/transactions/transactions.slice'
import { partitionDuplicates } from './duplicateHelpers'
// UI
import { TypographyStyles, TextColors } from '@/styles'
import { UploadScreenProps } from './index'
import { UploadBackButton } from '../UploadBackButton'
import { CurrencyFormatLabel } from '@/components/CurrencyFormatLabel'
import {
  ValidationErrorMessageContainer,
  UploadCSVErrorModel,
  generateValidationErrorModel,
} from './UploadCSVPanel'
import { Button, Input, Table, Text, Icon, AlertDialog, DatePicker } from '@/ui'
import { LockedBooksMessage } from '@/features/transactions/LockedBooksMessage'
import { UPLOAD_COPY } from '@/features/transactions/copyConstants'

interface TableRowProps {
  transactionModel: ManualTransactionModel
  editingIndex: number | null
  index: number
  setEditingIndex: (index: number | null) => void
  updateModelAtIndex: (props: {
    index: number
    model: ManualTransactionModel
  }) => void
}

const TableRow = ({
  transactionModel,
  editingIndex,
  index,
  setEditingIndex,
  updateModelAtIndex,
}: TableRowProps) => {
  const [date, setDate] = useState(transactionModel.date)
  const [amountInDollars, setAmountInDollars] = useState(
    transactionModel.amountInDollars
  )
  const [description, setDescription] = useState(transactionModel.description)

  const onEditClick = () => setEditingIndex(index)
  const onEndEditingClick = () => {
    updateModelAtIndex({
      index,
      model: {
        date,
        description: description.trim(),
        amountInDollars,
      },
    })
    setEditingIndex(null)
  }

  const isEditing = editingIndex === index
  return (
    <Table.Row
      data-state={isEditing ? 'selected' : undefined}
      // disabled={editingIndex !== null && !isEditing}
    >
      <Table.Cell>{index + 1}</Table.Cell>

      {!isEditing && <Table.Cell>{date}</Table.Cell>}

      {isEditing && (
        <Table.Cell>
          <DatePicker
            value={date}
            maxDate={new Date()}
            onChange={setDate}
            name="transactionDate"
            className="w-full max-w-full"
          />
        </Table.Cell>
      )}

      {!isEditing && <Table.Cell>{description}</Table.Cell>}

      {isEditing && (
        <Table.Cell>
          <Input
            placeholder="Enter a description"
            value={description}
            className="w-full max-w-full"
            onChange={(e) => setDescription(e.target.value)}
          />
        </Table.Cell>
      )}

      {!isEditing && (
        <Table.Cell textAlign="right">
          <CurrencyFormatLabel
            key={`currency-${index}`}
            value={amountInDollars}
          />
        </Table.Cell>
      )}

      {isEditing && (
        <Table.Cell textAlign="right" className="p-2">
          <Input
            className="w-full max-w-full"
            onChange={(e) => setAmountInDollars(e.target.value)}
            value={amountInDollars}
            placeholder="Amount"
            type="number"
          />
        </Table.Cell>
      )}

      <Table.Cell>
        {editingIndex === null && (
          <Icon
            icon={faPencil}
            onClick={onEditClick}
            size="sm"
            className="cursor-pointer"
          />
        )}
        {isEditing && (
          <Icon
            icon={faCheck}
            onClick={onEndEditingClick}
            size="sm"
            className="cursor-pointer"
          />
        )}
      </Table.Cell>
    </Table.Row>
  )
}

interface ModelStates {
  [i: number]: ManualTransactionModel
}

const UploadReviewPanel = ({
  goToScreenWithData,
  data,
  finishFlow,
  onUploadComplete,
}: UploadScreenProps) => {
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmInvoker, setConfirmInvoker] = useState('')
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [validationError, setValidationError] =
    useState<UploadCSVErrorModel | null>(null)
  const [modelStates, setModelStates] = useState<ModelStates>({})
  const [saving, setSaving] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)

  const dispatch = useAppDispatch()
  const currentUser = useReselector(getCurrentUser)

  const models = useMemo(() => data?.models || [], [data?.models])
  const { spreadsheetFile } = data || {}

  const titleText = UPLOAD_COPY.REVIEW_SCREEN_TITLE.replace(
    '{transaction_count}',
    models.length.toString()
  )

  useEffect(() => {
    const state: ModelStates = {}
    models.forEach((m, i) => {
      const candidateDate = moment(m.date)
      if (candidateDate.isValid()) {
        m.date = candidateDate.utc().format(DATE_FORMATS.INPUT)
      }
      m.description = m.description.trim()
      m.amountInDollars = m.amountInDollars.replace(/\$/, '')
      state[i] = m
    })

    setModelStates(state)
  }, [models])

  const openConfirm = (invoker: string) => {
    setConfirmInvoker(invoker)
    setConfirmOpen(true)
  }

  const updateModelAtIndex = ({
    index,
    model,
  }: {
    index: number
    model: ManualTransactionModel
  }) => {
    const stateClone = Object.values(modelStates).map((m) => {
      return { ...m }
    })
    model.amountInDollars = model.amountInDollars.replace(/\$/, '')
    model.description = model.description.trim()
    stateClone[index] = model
    setModelStates(stateClone)
  }

  const isEditing = editingIndex !== null

  const onContinueClick = async () => {
    setSaving(true)
    const modelList = Object.values(modelStates)

    try {
      const { errors, warnings } =
        await validateMultipleManualTransactions(modelList)

      if (errors.length) {
        const model = generateValidationErrorModel({ errors })
        setValidationError(model)
      } else {
        setValidationError(null)

        if (warnings.duplicateGroups.length > 0) {
          const { validTransactions, duplicateGroups } = partitionDuplicates(
            modelList,
            warnings.duplicateGroups
          )

          goToScreenWithData({
            index: 3,
            data: {
              validTransactions,
              duplicateGroups,
              spreadsheetFile,
            },
          })
        } else {
          // No duplicate warnings, upload the file and save transactions to DB
          const result =
            await createMultipleManualTransactions(modelList)(dispatch)
          if (currentUser && spreadsheetFile) {
            const docRes = await userUploadDocument({
              file: spreadsheetFile,
              user: currentUser,
              documentType: 'other',
            })(dispatch)
            if (docRes && 'error' in docRes) {
              throw new Error(docRes.error)
            }
          }

          if (result) {
            onUploadComplete?.({ success: true, data: modelList })
          } else {
            onUploadComplete?.({ success: false })
          }
          finishFlow()
        }
      }
      setServerError(null)
    } catch (err) {
      setServerError(parseErrorFromCatch(err))
    }
    setSaving(false)
  }

  return (
    <>
      <div className="flex flex-col gap-2">
        <div className="flex-1">
          <UploadBackButton
            disabled={saving}
            onClick={() => openConfirm('back')}
          />
        </div>
        <Text
          variant={TypographyStyles.H1}
          color={TextColors.MOSS}
          className="flex-1"
        >
          {titleText}
        </Text>
        <Text variant={TypographyStyles.BODY_MD} className="flex-1">
          {UPLOAD_COPY.REVIEW_SCREEN_SUBTITLE}
        </Text>
        <Text variant={TypographyStyles.BODY_MD} className="flex-1">
          {UPLOAD_COPY.SIGN_EXPLAINER}
        </Text>
        {currentUser?.booksLockedForUserYear && (
          <div className="flex-1">
            <LockedBooksMessage year={currentUser.booksLockedForUserYear} />
          </div>
        )}
        <div className="w-full max-h-[40vh] overflow-x-auto overflow-y-scroll border border-neutral-50 py-2 rounded-md">
          <Table className="w-full">
            <Table.Header>
              <Table.Row className="border-none text-xs sm:text-sm">
                {/* leading sentinel — hide on phones */}
                <Table.HeaderCell className="hidden sm:table-cell w-1/12" />

                {/* Date — full-width on phones, 24 % from sm up */}
                <Table.HeaderCell className="w-full sm:w-3/12">
                  Date
                </Table.HeaderCell>

                {/* Description — full-width on phones, 35 % from sm up */}
                <Table.HeaderCell className="w-full sm:w-3/12">
                  Description
                </Table.HeaderCell>

                {/* Amount — full-width on phones, 29 % from sm up */}
                <Table.HeaderCell className="text-right w-full sm:w-3/12">
                  Amount
                </Table.HeaderCell>

                {/* trailing sentinel — hide on phones */}
                <Table.HeaderCell className="hidden sm:table-cell w-2/12" />
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {Object.values(modelStates).map((m, i) => (
                <TableRow
                  key={`csv-table-row-${m.id || m.date}-${i}`}
                  setEditingIndex={setEditingIndex}
                  transactionModel={m}
                  index={i}
                  editingIndex={editingIndex}
                  updateModelAtIndex={updateModelAtIndex}
                />
              ))}
            </Table.Body>
          </Table>
        </div>
        {!validationError && (
          <Text
            variant={TypographyStyles.BODY_MD}
            className="flex-1 text-center"
          >
            Does everything look correct?
          </Text>
        )}

        {validationError && (
          <div className="flex-1">
            <ValidationErrorMessageContainer error={validationError} />
          </div>
        )}

        {serverError && (
          <Text
            variant={TypographyStyles.BODY_MD}
            className="flex-1 text-right"
          >
            {serverError}
          </Text>
        )}

        <Button
          loading={saving}
          onClick={onContinueClick}
          disabled={isEditing || saving}
          className="flex-1 mt-4 w-full"
        >
          {!isEditing
            ? 'Continue'
            : 'Click the check icon to finish editing the row'}
        </Button>
      </div>

      <AlertDialog open={confirmOpen}>
        <AlertDialog.Content className="max-w-sm z-10000">
          <AlertDialog.Header>
            <AlertDialog.Title>
              {`Are you sure you want to ${
                confirmInvoker === 'back' ? 'go back' : 'close'
              }?`}
            </AlertDialog.Title>
          </AlertDialog.Header>
          <AlertDialog.Description>
            {`You will lose any uploaded data if you ${
              confirmInvoker === 'back' ? 'go back' : 'proceed with closing'
            }.`}
          </AlertDialog.Description>
          <AlertDialog.Footer className="gap-2">
            <AlertDialog.Cancel onClick={() => setConfirmOpen(false)}>
              Cancel
            </AlertDialog.Cancel>
            <AlertDialog.Action
              onClick={() => {
                if (confirmInvoker === 'back') {
                  goToScreenWithData({ index: 1 })
                  return
                }

                setConfirmOpen(false)
                finishFlow()
              }}
            >
              Close
            </AlertDialog.Action>
          </AlertDialog.Footer>
        </AlertDialog.Content>
      </AlertDialog>
    </>
  )
}

export const UploadReviewModal = (props: UploadScreenProps) => (
  <UploadReviewPanel {...props} />
)
